/**
 * 🔴 W72 STEP 4 — ONE MOVEMENT'S HISTORY, BUILT THE WAY THE PHONE BUILDS IT. The TypeScript TWIN of
 * `ExerciseProgress.entry(from:)` (the one entry builder), `ProgressHistoryMerge.oneReadingPerDay` and
 * `ProgressHistoryMerge.foldedByName`, proven against a corpus the SWIFT printed (`tools/progress-corpus`,
 * `__fixtures__/progressCorpus.json`) — never against hand-typed expectations.
 *
 * ⚠️ WHAT THIS REPLACED WAS A SECOND READER THAT COUNTED WARM-UPS. MEASURED 2026-09-25 on the render fixture: 209 of
 * 755 logged sets are `warmup` rungs and 174 of 525 session-exercises sit in warm-up or cool-down sections, and the
 * copied Metrics screen folded every one of them into the lift's history. The phone's rule (`SetLogKind.isWorking`,
 * `ProgressListFilter.includes`) is the one kept here, so the coach reads the numbers the athlete reads.
 *
 * The rules' reasons, with his words and the measurements, are in the Swift sources; they are not restated here.
 */

import { durationLabel, kg, runPhrase } from "./phoneDose";
import { estimatedOneRepMax } from "./epley";
import type { OneRMSet } from "./oneRepMax";
import { startOfDay } from "./progressChart";

export type LoggedSet = {
  readonly setNumber: number;
  readonly status: string;
  readonly reps: number | null;
  readonly weightKg: number | null;
  readonly holdSeconds: number | null;
  /** W125 — read by the exact 1RM only (`set_logs.rpe`, `assist_kg`, `modifier_label_override`); absent = not known. */
  readonly rpe?: number | null;
  readonly assistKg?: number | null;
  readonly band?: string | null;
  /** W136 B2 S3r5 P2 — `set_logs.distance_m` / `rounds`: a set carrying either is a run or a rounds set (D7); absent = none. */
  readonly distanceM?: number | null;
  readonly rounds?: number | null;
};

export type PerformedSet = {
  readonly setNumber: number;
  readonly reps: number | null;
  readonly weightKg: number | null;
  /** On a run or a rounds set (a distance or rounds below), the time the athlete TYPED — never a hold (D7). */
  readonly holdSeconds: number | null;
  readonly distanceM?: number | null;
  readonly rounds?: number | null;
};

/** `SessionReadout.PerformedSet.isTyped` — a distance or rounds makes the set's time a typed one. */
export function isTyped(s: { readonly distanceM?: number | null; readonly rounds?: number | null }): boolean {
  return (s.distanceM ?? null) !== null || (s.rounds ?? null) !== null;
}

export type Entry = {
  readonly date: number;
  readonly topWeightKg: number | null;
  readonly bestReps: number | null;
  readonly bestHoldSeconds: number | null;
  readonly volumeKg: number;
  readonly sets: number;
  readonly performed: readonly PerformedSet[];
  readonly exerciseInstanceId: string;
  readonly sessionId: string;
  readonly bestE1RMKg: number | null;
  /** W125 — the working sets as `oneRepMax.ts` reads them; read only for a coach's exercise with its `1RM` switch on. */
  readonly oneRMSets?: readonly OneRMSet[];
};

export type ExerciseHistory = { readonly id: string; readonly name: string; readonly entries: readonly Entry[] };

/** `SetLogKind.performed` — a POSITIVE list, so an unknown spelling is never performed by accident. */
export function isPerformed(status: string): boolean {
  return status === "completed" || status === "partial" || status === "warmup";
}

/** `SetLogKind.isWorking` — performed, and not a rung. */
export function isWorking(status: string): boolean {
  return isPerformed(status) && status !== "warmup";
}

/** `ProgressListFilter.includes(sectionRole:)` — main work only; a row that recorded no section stays. */
export function sectionIncludes(role: string | null): boolean {
  return role === null || (role !== "warm_up" && role !== "cool_down");
}

const maxOf = (xs: readonly number[]): number | null => (xs.length === 0 ? null : Math.max(...xs));
const present = <T,>(xs: readonly (T | null)[]): T[] => xs.filter((x): x is T => x !== null);

/** `ExerciseProgress.entry(from:)` — the working sets only; each maximum from real sets; Epley PER SET. */
export function entryFromLogs(logs: readonly LoggedSet[], date: number, exerciseInstanceId: string, sessionId: string): Entry | null {
  const working = logs.filter((l) => isWorking(l.status));
  if (working.length === 0) return null;
  // W136 B2 S3r5 P2 — a rounds set's reps are the part-round's: no Best set, Volume or 1RM (`ExerciseProgress.entry`).
  const counted = working.filter((l) => (l.rounds ?? null) === null);
  return {
    date,
    topWeightKg: maxOf(present(working.map((l) => l.weightKg))),
    bestReps: maxOf(present(counted.map((l) => l.reps))),
    // W136 B2 S3r5 P2 (D7) — a run's / rounds set's time is TYPED, never a hold (`ExerciseProgress.entry`).
    bestHoldSeconds: maxOf(present(working.filter((l) => !isTyped(l)).map((l) => l.holdSeconds))),
    volumeKg: counted.reduce((s, l) => s + (l.weightKg ?? 0) * (l.reps ?? 0), 0),
    sets: working.length,
    performed: working.map((l) => ({ setNumber: l.setNumber, reps: l.reps, weightKg: l.weightKg, holdSeconds: l.holdSeconds,
                                     distanceM: l.distanceM ?? null, rounds: l.rounds ?? null })),
    exerciseInstanceId,
    sessionId,
    // The divisor is the GENERATED one (`epley.ts`, emitted from the engine's Swift) — never a second typed 30.
    bestE1RMKg: maxOf(present(counted.map((l) => estimatedOneRepMax(l.weightKg, l.reps)))),
    oneRMSets: counted.map((l) => ({ reps: l.reps, weightKg: l.weightKg, rpe: l.rpe ?? null, assistKg: l.assistKg ?? null,
                                     band: l.band === undefined ? "" : l.band })),
  };
}

const byDate = (a: Entry, b: Entry) => a.date - b.date;

/** `ProgressHistoryMerge.oneReadingPerDay` — per day, the fullest SESSION RECORD wins; ties go to the latest. */
export function oneReadingPerDay(exercises: readonly ExerciseHistory[]): ExerciseHistory[] {
  return exercises.map((exercise) => {
    const dayOrder: number[] = [];
    const byDay = new Map<number, Entry[]>();
    for (const e of exercise.entries) {
      const day = startOfDay(e.date);
      if (!byDay.has(day)) dayOrder.push(day);
      byDay.set(day, [...(byDay.get(day) ?? []), e]);
    }
    const kept: Entry[] = [];
    for (const day of dayOrder) {
      const onDay = byDay.get(day) ?? [];
      const sessionOrder: string[] = [];
      const bySession = new Map<string, Entry[]>();
      for (const e of onDay) {
        if (!bySession.has(e.sessionId)) sessionOrder.push(e.sessionId);
        bySession.set(e.sessionId, [...(bySession.get(e.sessionId) ?? []), e]);
      }
      if (sessionOrder.length <= 1) { kept.push(...onDay); continue; }
      // Swift's `max(by:)` keeps the LATER of two equal elements; `a < b` below is its predicate.
      const less = (a: string, b: string): boolean => {
        const ea = bySession.get(a) ?? [], eb = bySession.get(b) ?? [];
        const sa = ea.reduce((s, x) => s + x.sets, 0), sb = eb.reduce((s, x) => s + x.sets, 0);
        if (sa !== sb) return sa < sb;
        const da = Math.max(...ea.map((x) => x.date)), db = Math.max(...eb.map((x) => x.date));
        if (da !== db) return da < db;
        return true;
      };
      let best = sessionOrder[0] as string;
      for (const s of sessionOrder.slice(1)) if (less(best, s)) best = s;   // Swift: `if areInIncreasingOrder(result, e) { result = e }`
      kept.push(...(bySession.get(best) ?? onDay));
    }
    return { id: exercise.id, name: exercise.name, entries: [...kept].sort(byDate) };
  });
}

/** `ProgressHistoryMerge.foldedByName` — one movement, two libraries, one row (the newest library's id). */
export function foldedByName(exercises: readonly ExerciseHistory[]): ExerciseHistory[] {
  const byName = new Map<string, ExerciseHistory>();
  const order: string[] = [];
  const newest = (x: ExerciseHistory) => (x.entries.length === 0 ? -Infinity : Math.max(...x.entries.map((e) => e.date)));
  for (const e of exercises) {
    const key = e.name.trim().toLowerCase();
    if (key === "") { byName.set(`id:${e.id}`, e); order.push(`id:${e.id}`); continue; }
    const existing = byName.get(key);
    if (existing === undefined) { byName.set(key, e); order.push(key); continue; }
    const newerFirst = newest(e) > newest(existing) ? e : existing;
    const other = newerFirst.id === e.id ? existing : e;
    const known = new Set(newerFirst.entries.map((x) => x.exerciseInstanceId));
    const extra = other.entries.filter((x) => !known.has(x.exerciseInstanceId));
    byName.set(key, { id: newerFirst.id, name: newerFirst.name, entries: [...newerFirst.entries, ...extra].sort(byDate) });
  }
  return order.map((k) => byName.get(k)).filter((x): x is ExerciseHistory => x !== undefined);
}

/* ── the web loader's shape conversion (what `routes/ClientProgress` hands in) ───────────────────── */

export type HistoryInstance = {
  readonly key: string;
  readonly name: string;
  readonly instanceId: string;
  readonly sessionId: string;
  readonly date: number;
  readonly logs: readonly LoggedSet[];
};

/**
 * WHEN AN APPEARANCE HAPPENED — a web-loader decision, stated: the EARLIEST `set_logs.completed_at` of the instance
 * (the thing that records somebody doing something; 400 of 400 populated where `scheduled_date` was 0 of 40), else
 * the session's scheduled / completed / created date. `tools/progress-corpus/build.py` decides the corpus's dates
 * the same way.
 */
export function instanceDate(logStamps: readonly (number | null)[], session: { scheduled: number | null; completed: number | null; created: number | null }): number | null {
  const stamps = present(logStamps);
  if (stamps.length > 0) return Math.min(...stamps);
  return session.scheduled ?? session.completed ?? session.created;
}

/** Instances → per-movement histories in first-appearance order → the fold → one reading per day. */
export function buildHistories(instances: readonly HistoryInstance[]): ExerciseHistory[] {
  const order: string[] = [];
  const byKey = new Map<string, { id: string; name: string; entries: Entry[] }>();
  for (const i of instances) {
    const e = entryFromLogs(i.logs, i.date, i.instanceId, i.sessionId);
    if (e === null) continue;
    let x = byKey.get(i.key);
    if (x === undefined) { x = { id: i.key, name: i.name, entries: [] }; byKey.set(i.key, x); order.push(i.key); }
    x.entries.push(e);
  }
  const built = order.map((k) => {
    const x = byKey.get(k) as { id: string; name: string; entries: Entry[] };
    return { id: x.id, name: x.name, entries: [...x.entries].sort(byDate) };
  });
  return oneReadingPerDay(foldedByName(built));
}

/** Most recently trained first — the order the phone's page uses. */
export function mostRecentFirst(xs: readonly ExerciseHistory[]): ExerciseHistory[] {
  const last = (x: ExerciseHistory) => x.entries[x.entries.length - 1]?.date ?? -Infinity;
  return [...xs].sort((a, b) => last(b) - last(a));
}

/* ── what one dot stands for, in words (`SessionReadout.line`) ──────────────────────────────────── */


/** `EnginePerSet.column` — a field across the day's sets is COMPLETE or EMPTY, never partial. */
function column<T>(values: readonly (T | null)[]): T[] {
  return values.every((v) => v !== null) ? (values as T[]) : [];
}

/**
 * `SessionReadout.line` — the day's sets in the words their prescription used (`AssistLoad.prescriptionLine` with no
 * rep range, no target load, no types: every set here is working by construction). Ordered by set number; a
 * partial column is refused whole so the sentence weakens rather than lies. Proven against the Swift's own lines.
 */
export function readoutLine(sets: readonly PerformedSet[], distanceKm = false): string {
  const ordered = [...sets].sort((a, b) => a.setNumber - b.setNumber);
  if (ordered.length === 0) return "";
  const reps = column(ordered.map((s) => s.reps));
  // W136 B2 S3r5 P2 — a run or a rounds day is said in §5's words (`runPhrase`, the Swift's twin); its time is TYPED,
  // so it never becomes the hold head (`SessionReadout.line`).
  const typed = ordered.some(isTyped);
  const secs = typed ? [] : column(ordered.map((s) => s.holdSeconds));
  const run = typed
    ? runPhrase(ordered.length, column(ordered.map((s) => s.distanceM ?? null)), column(ordered.map((s) => s.rounds ?? null)),
                reps, column(ordered.map((s) => s.holdSeconds)), distanceKm)
    : null;
  const weights = column(ordered.map((s) => s.weightKg));
  const spoken = <T,>(values: T[], unit: string, format: (v: T) => string): string | null => {
    const first = values[0];
    if (first === undefined) return null;
    if (values.every((v) => v === first)) return `${String(values.length)} × ${format(first)}${unit}`;
    return values.map(format).join("/") + unit;
  };
  const head = run ?? spoken(reps, " reps", (v) => String(v)) ?? spoken(secs, "", durationLabel)
    ?? (ordered.length === 1 ? "1 set" : `${String(ordered.length)} sets`);
  let load: string | null = null;
  const firstW = weights[0];
  if (firstW !== undefined && !weights.every((w) => w === firstW)) load = `@ ${weights.map(kg).join("/")} kg`;
  else if (firstW !== undefined && firstW > 0) load = `@ ${kg(firstW)} kg`;
  return load === null ? head : `${head} ${load}`;
}
